import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { TFunction } from "i18next";
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useInViewOnce } from "@/hooks/useInViewOnce";
import { useCountUp } from "@/lib/motion";

import {
  formatCompetitorsOfTotal,
  formatInvestmentMultiple,
  formatPercent,
  type PersonaOpportunityDisplayRow,
} from "../../../lib/persona-opportunity-row-model.js";
import { PostFlightWindowContext } from "../../shared/PostFlightWindowContext.js";
import { postFlightWindowContextFromMetrics } from "../../../lib/post-flight-opportunity-metrics.js";
import { PostFlightMetricChips } from "./PostFlightMetricChips.js";

/** Matches the bar's `duration-[600ms]` so the counting figure lands with the fill. */
const SHARE_BAR_FILL_DURATION_MS = 600;
/** The You bar trails Market so the gap between them reads as it opens. */
const SHARE_BAR_STAGGER_MS = 80;
/** Keeps the scale tooltip clear of the pointer so it can track without flicker. */
const TOOLTIP_CURSOR_OFFSET_X = 14;
const TOOLTIP_CURSOR_OFFSET_Y = 18;
const TOOLTIP_VIEWPORT_MARGIN = 8;

function drivenFeatureLabel(t: TFunction, drivenBy: PersonaOpportunityDisplayRow["drivenBy"]) {
  if (drivenBy === "motivation") {
    return t("personaOpportunities.active.kpi.featureMotivation", "motivation");
  }
  if (drivenBy === "painpoint") {
    return t("personaOpportunities.active.kpi.featurePainPoint", "pain point");
  }
  return t("personaOpportunities.active.kpi.featureAngle", "angle");
}

/** Bar width (0–100) relative to the larger share, so the leader always fills the track. */
function toRelativeWidth(value: number, max: number): number {
  if (!Number.isFinite(value) || max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

function DrivenByTag({ drivenBy }: { drivenBy: PersonaOpportunityDisplayRow["drivenBy"] }) {
  const { t } = useTranslation();
  if (!drivenBy) return null;

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
      <Icon name="target" size="xs" color="inherit" aria-hidden />
      {drivenBy === "painpoint"
        ? t("personaOpportunities.active.ledByPainPoint", "Pain point–led")
        : t("personaOpportunities.active.ledByMotivation", "Motivation–led")}
    </span>
  );
}

function DriverBlock({
  label,
  text,
  isDriving,
}: {
  label: string;
  text: string;
  isDriving: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-0.5 border-l-2 pl-2.5",
        isDriving ? "border-primary" : "border-border",
      )}
    >
      <span
        className={cn(
          "text-[10px] font-semibold uppercase tracking-[0.08em]",
          isDriving ? "text-primary" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "text-[13px] leading-[1.4]",
          isDriving ? "font-medium text-foreground" : "text-muted-foreground",
        )}
      >
        {text}
      </span>
    </div>
  );
}

function ShareBar({
  label,
  share,
  widthPct,
  isRevealed,
  delayMs = 0,
  fillClassName,
  valueClassName,
}: {
  label: string;
  /** Fraction 0–1, shown as a whole percent. */
  share: number;
  widthPct: number;
  /** Grows the bar and counts the figure up from zero once true. */
  isRevealed: boolean;
  delayMs?: number;
  fillClassName: string;
  valueClassName?: string;
}) {
  const isMeasurable = Number.isFinite(share);
  const displayPct = useCountUp(isRevealed && isMeasurable ? Math.round(share * 100) : 0, {
    durationMs: SHARE_BAR_FILL_DURATION_MS,
    delayMs,
  });

  return (
    <div className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-[11px] font-medium text-muted-foreground">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        {/* Scaled rather than sized so the grow-in stays on the compositor. */}
        <div
          className={cn(
            "h-full w-full origin-left rounded-full transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
            fillClassName,
          )}
          style={{
            transform: `scaleX(${isRevealed ? widthPct / 100 : 0})`,
            transitionDelay: delayMs > 0 ? `${delayMs}ms` : undefined,
          }}
        />
      </div>
      <span
        className={cn(
          "w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-foreground",
          valueClassName,
        )}
      >
        {isMeasurable ? formatPercent(displayPct / 100) : formatPercent(share)}
      </span>
    </div>
  );
}

function writeTooltipPosition(tip: HTMLElement, x: number, y: number) {
  const left = x + TOOLTIP_CURSOR_OFFSET_X;
  const top = y + TOOLTIP_CURSOR_OFFSET_Y;
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;

  const rect = tip.getBoundingClientRect();
  const maxLeft = Math.max(
    TOOLTIP_VIEWPORT_MARGIN,
    window.innerWidth - rect.width - TOOLTIP_VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    TOOLTIP_VIEWPORT_MARGIN,
    window.innerHeight - rect.height - TOOLTIP_VIEWPORT_MARGIN,
  );
  tip.style.left = `${Math.min(Math.max(TOOLTIP_VIEWPORT_MARGIN, left), maxLeft)}px`;
  tip.style.top = `${Math.min(Math.max(TOOLTIP_VIEWPORT_MARGIN, top), maxTop)}px`;
}

/** Explains the relative scale and tracks the pointer while the bars are hovered. */
function ShareScaleHint({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tooltipId = useId();
  const tipRef = useRef<HTMLDivElement>(null);
  const pointRef = useRef<{ x: number; y: number } | null>(null);
  const isHoveringRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  const explanation = t(
    "personaOpportunities.active.shareBarScaleTooltip",
    "The bar is scaled to the larger share, so the leader fills the track. The percent is how much of the investment that side actually has.",
  );

  function place(x: number, y: number) {
    pointRef.current = { x, y };
    const tip = tipRef.current;
    if (!tip) {
      setIsOpen(true);
      return;
    }
    writeTooltipPosition(tip, x, y);
  }

  useLayoutEffect(() => {
    if (!isOpen || !pointRef.current || !tipRef.current) return;
    writeTooltipPosition(tipRef.current, pointRef.current.x, pointRef.current.y);
  }, [isOpen]);

  return (
    <>
      <div
        role="group"
        tabIndex={0}
        aria-label={t(
          "personaOpportunities.active.shareBarScaleLabel",
          "Investment share comparison",
        )}
        aria-describedby={isOpen ? tooltipId : undefined}
        className="flex cursor-help flex-col gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        onPointerMove={(event) => {
          isHoveringRef.current = true;
          place(event.clientX, event.clientY);
        }}
        onPointerLeave={() => {
          isHoveringRef.current = false;
          setIsOpen(false);
        }}
        onFocus={(event) => {
          if (!event.currentTarget.matches(":focus-visible")) return;
          const rect = event.currentTarget.getBoundingClientRect();
          place(rect.left, rect.top);
        }}
        onBlur={() => {
          if (isHoveringRef.current) return;
          setIsOpen(false);
        }}
      >
        {children}
      </div>
      {isOpen
        ? createPortal(
            <div
              ref={tipRef}
              id={tooltipId}
              role="tooltip"
              className="pointer-events-none fixed z-50 max-w-[280px] rounded-md bg-tooltip px-3 py-1.5 text-xs text-tooltip-foreground shadow-md"
            >
              {explanation}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function InvestmentGapPanel({ row }: { row: PersonaOpportunityDisplayRow }) {
  const { t } = useTranslation();
  const [panelRef, isInView] = useInViewOnce<HTMLDivElement>();
  const investmentLabel = formatInvestmentMultiple(row.investmentIndex);
  const hasNoInvestment = row.myInvestmentSharePct <= 0;
  const maxShare = Math.max(row.marketInvestmentSharePct, row.myInvestmentSharePct);

  const headline = investmentLabel
    ? t("personaOpportunities.active.investmentHeadline", {
        multiple: investmentLabel.replace(/\s*more$/i, ""),
        defaultValue: "The market invests {{multiple}} more here than you do",
      })
    : hasNoInvestment
      ? t(
          "personaOpportunities.active.investmentWhitespace",
          "Open whitespace — you don’t invest here yet",
        )
      : t("personaOpportunities.active.investmentUnavailable", "Investment index unavailable");

  return (
    <div
      ref={panelRef}
      className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted/40 p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-semibold leading-[1.35] text-foreground">{headline}</p>
        {hasNoInvestment ? (
          <span className="shrink-0 rounded-sm bg-foreground px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background">
            {t("personaOpportunities.active.gapBadge", "Gap")}
          </span>
        ) : null}
      </div>
      <ShareScaleHint>
        <ShareBar
          label={t("personaOpportunities.active.shareMarketLabel", "Market")}
          share={row.marketInvestmentSharePct}
          widthPct={toRelativeWidth(row.marketInvestmentSharePct, maxShare)}
          isRevealed={isInView}
          fillClassName="bg-violet-500"
        />
        <ShareBar
          label={t("personaOpportunities.active.shareYouLabel", "You")}
          share={row.myInvestmentSharePct}
          widthPct={toRelativeWidth(row.myInvestmentSharePct, maxShare)}
          isRevealed={isInView}
          delayMs={SHARE_BAR_STAGGER_MS}
          fillClassName="bg-primary"
          valueClassName="text-primary"
        />
      </ShareScaleHint>
    </div>
  );
}

function EvidenceStat({
  label,
  value,
  note,
  tooltip,
}: {
  label: string;
  value: string;
  note: string;
  tooltip: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 bg-card px-3 py-2.5">
      <span className="text-[19px] font-semibold leading-none tracking-[-0.01em] tabular-nums text-foreground">
        {value}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-foreground/80">
            {label}
            <Icon name="info" size="xs" className="size-2.5 shrink-0 opacity-60" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[190px]">
          {tooltip}
        </TooltipContent>
      </Tooltip>
      <span className="truncate text-[11px] leading-[1.3] text-muted-foreground">{note}</span>
    </div>
  );
}

/** Competitors / creatives / stick rate / duplicated — the evidence behind the opportunity. */
export function PersonaOpportunityEvidenceStrip({
  row,
  className,
}: {
  row: PersonaOpportunityDisplayRow;
  className?: string;
}) {
  const { t } = useTranslation();
  const feature = drivenFeatureLabel(t, row.drivenBy);

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4",
          className,
        )}
      >
        <EvidenceStat
          label={t("personaOpportunities.active.kpi.competitors", "Competitors")}
          value={formatCompetitorsOfTotal(row.competitorsRunningCount, row.competitorsTotal)}
          note={t("personaOpportunities.active.kpi.competitorsNote", {
            feature,
            defaultValue: "run this {{feature}}",
          })}
          tooltip={t("personaOpportunities.active.kpi.competitorsTooltip", {
            feature,
            defaultValue: "How many selected competitors run creatives in this {{feature}} cluster.",
          })}
        />
        <EvidenceStat
          label={t("personaOpportunities.active.kpi.stickRate", "Stick rate")}
          value={formatPercent(row.stickRatePct)}
          note={t("personaOpportunities.active.kpi.stickRateNote", "stayed live")}
          tooltip={t(
            "personaOpportunities.active.kpi.stickRateTooltip",
            "Share of creatives that stayed live through the stick window.",
          )}
        />
        <EvidenceStat
          label={t("personaOpportunities.active.kpi.creatives", "Creatives")}
          value={String(row.creativesCount)}
          note={t("personaOpportunities.active.kpi.creativesNote", "observed")}
          tooltip={t("personaOpportunities.active.kpi.creativesTooltip", {
            feature,
            defaultValue: "Number of competitor creatives observed for this {{feature}}.",
          })}
        />
        <EvidenceStat
          label={t("personaOpportunities.active.kpi.duplicated", "Duplicated")}
          value={formatPercent(row.duplicatedPct)}
          note={t("personaOpportunities.active.kpi.duplicatedNote", "across ads")}
          tooltip={t("personaOpportunities.active.kpi.duplicatedTooltip", {
            feature,
            defaultValue:
              "Percent of creatives that were duplicated across different ads in the {{feature}} cluster.",
          })}
        />
      </div>
    </TooltipProvider>
  );
}

/** Driver tag, angle, pain/motivation, investment gap, and evidence — shared by the card and the hub drawer. */
export function PersonaOpportunityInsight({
  row,
  headerAction,
  variant = "drawer",
}: {
  row: PersonaOpportunityDisplayRow;
  /** Sits at the end of the header row, e.g. the card's selection checkbox. */
  headerAction?: ReactNode;
  /** `card` omits the why line and leaves the evidence strip for the card to render full-width. */
  variant?: "card" | "drawer";
}) {
  const { t } = useTranslation();
  const hasHeader = Boolean(row.drivenBy) || Boolean(headerAction);
  const isDrawer = variant === "drawer";

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3.5">
      {hasHeader ? (
        <div className="flex min-h-5 items-center justify-between gap-2">
          <DrivenByTag drivenBy={row.drivenBy} />
          {headerAction}
        </div>
      ) : null}

      <h3 className="text-[17px] font-semibold leading-[1.3] tracking-[-0.01em] text-foreground">
        {row.angle}
      </h3>

      {row.fromLabel ? (
        <p className="text-[12px] text-muted-foreground">
          {t("personaOpportunities.postFlightOpportunity.conceptLabel", {
            concept: row.fromLabel,
            defaultValue: "Concept: {{concept}}",
          })}
        </p>
      ) : null}

      {row.postFlight?.instruction &&
      row.postFlight.instruction.trim() !== row.angle.trim() ? (
        <p className="text-[13px] leading-[1.4] text-foreground">{row.postFlight.instruction}</p>
      ) : null}

      {isDrawer || row.fromLabel ? (
        row.why ? <p className="text-[13px] text-muted-foreground">{row.why}</p> : null
      ) : null}

      {row.postFlight ? (
        <>
          <PostFlightWindowContext data={postFlightWindowContextFromMetrics(row.postFlight)} />
          <PostFlightMetricChips metrics={row.postFlight} />
        </>
      ) : null}

      {row.painPoint || row.motivation ? (
        <div className="flex flex-col gap-2">
          {row.painPoint ? (
            <DriverBlock
              label={t("personaOpportunities.active.painPoint", "Pain point")}
              text={row.painPoint}
              isDriving={row.drivenBy === "painpoint"}
            />
          ) : null}
          {row.motivation ? (
            <DriverBlock
              label={t("personaOpportunities.active.motivation", "Motivation")}
              text={row.motivation}
              isDriving={row.drivenBy === "motivation"}
            />
          ) : null}
        </div>
      ) : null}

      {row.fromLabel ? null : (
        <div className="mt-auto flex flex-col gap-3">
          {/* Keyed so the open hub drawer replays the reveal when it switches opportunity. */}
          <InvestmentGapPanel key={row.id} row={row} />
          {isDrawer ? <PersonaOpportunityEvidenceStrip row={row} /> : null}
        </div>
      )}
    </div>
  );
}
